import React, {useState, useEffect} from 'react';
import {useSelector} from 'react-redux';
import styled from 'styled-components';
import NumberFormat from 'react-number-format';
import {round} from 'lodash';
import {applyRound, getPercent} from '@ey/utils';
import {getCurrentMateriality, getPriorYearMateriality} from './../../../../../../selectors/Ares/materiality';
import {labels, setNumberRounding, setThousandSeparator, isNullOrUndefined} from '../../../../../../util/utils';
import {materialitySADpercentageDecimalAmmount as sadPercentageDecimalAmmount} from '../../../../../../util/uiconstants';
import {useEngagementId, useUserPreferences} from '../../../../../../util/customHooks';
import EllipsesControl from '../../../../EllipsesControl/EllipsesControl';
import AresSpecialBodyContainer from './../AresSpecialBodyContainer';

export default function MaterialityViewer({
	bodyContent,
	headerId,
	sectionId,
	bodyId,
	updateBodyOptions,
	getBodiesBySectionId,
	isSnapshotView,
	showGuidance
}) {
	const [newMateriality, setNewMateriality] = useState([]);

	const priorYearMateriality = useSelector(getPriorYearMateriality),
		currentMateriality = useSelector(getCurrentMateriality),
		userPreferences = useUserPreferences();

	const engagementId = useEngagementId();

	const getBasisAmount = (materiality) => {
		let selectedMateriality;
		if (materiality.isPriorYear) {
			selectedMateriality = priorYearMateriality;
		} else {
			selectedMateriality = currentMateriality;
		}

		const basisOrForecastedAmount = !isNullOrUndefined(selectedMateriality?.basisAdjustedAmount)
			? selectedMateriality.basisAdjustedAmount
			: selectedMateriality?.basisForecastAmount;

		const basisAmount = !isNullOrUndefined(basisOrForecastedAmount) ? Math.abs(basisOrForecastedAmount) : null;
		return basisAmount;
	};

	const displayValue = (value, suffix, isDecimal, decimalScale = 1) => {
		const valFormatter = setThousandSeparator(userPreferences.numberFormat);
		if (value) {
			const formattedNumber = (
				<NumberFormat
					value={isDecimal ? value : setNumberRounding(userPreferences.roundingFormat, value)}
					thousandSeparator={valFormatter.thousandSeparator}
					decimalSeparator={valFormatter.decimalSeparator}
					thousandsGroupStyle={valFormatter.thousandsGroupStyle}
					decimalScale={isDecimal ? decimalScale : 0}
					fixedDecimalScale={!isDecimal}
					displayType="text"
					suffix={''}
				/>
			);

			const formattedNumbertooltip = (
				<NumberFormat
					value={isDecimal ? value : setNumberRounding(userPreferences.roundingFormat, value)}
					thousandSeparator={valFormatter.thousandSeparator}
					decimalSeparator={valFormatter.decimalSeparator}
					thousandsGroupStyle={valFormatter.thousandsGroupStyle}
					decimalScale={isDecimal ? decimalScale : 0}
					fixedDecimalScale={!isDecimal}
					displayType="text"
					suffix={isDecimal ? suffix : ''}
				/>
			);
			return <section className="DisplayAmountCount">{formattedNumber}</section>;
		} else {
			return <span className="DisplayAmountCount">{'--'}</span>;
		}
	};

	const getPercentage = (partialValue, totalValue) => {
		return totalValue > 0 ? getPercent(partialValue, totalValue) : 0;
	};

	const displayPMPercentage = (partialValue, totalValue) => {
		if (partialValue && totalValue) {
			const _value = round((partialValue / totalValue) * 100, 1);
			return (
				<>
					{'('}
					{displayValue(_value, '%', true)} {'%)'}
				</>
			);
		} else {
			return <span className="DisplayAmountCount">{'(-- %)'}</span>;
		}
	};

	const displayTEPercentage = (partialValue, totalValue) => {
		if (partialValue && totalValue) {
			const _value = getPercentage(partialValue, totalValue);

			// If result is between 74% and 76%, show 75%
			if (_value && _value >= 74 && _value <= 76) {
				return (
					<>
						{'('}
						{displayValue(75, '%', true)}
						{'%)'}
					</>
				);
			}
			// If result is between 49% and 51%, show 50%
			else if (_value && _value >= 49 && _value <= 51) {
				return (
					<>
						{'('}
						{displayValue(50, '%', true)}
						{'%)'}
					</>
				);
			} else {
				return (
					<>
						{'('}
						{displayValue(_value, '%', true)}
						{'%)'}
					</>
				);
			}
		} else {
			return <span className="DisplayAmountCount">{'(-- %)'}</span>;
		}
	};

	const displaySADPercentage = (partialValue, totalValue) => {
		if (partialValue && totalValue) {
			const roundVal = applyRound((partialValue * 100) / totalValue, sadPercentageDecimalAmmount);
			return (
				<>
					{'('}
					{displayValue(roundVal, '%', true, sadPercentageDecimalAmmount)}
					{'%)'}
				</>
			);
		} else {
			return <span className="DisplayAmountCount">{'(-- %)'}</span>;
		}
	};

	useEffect(() => {
		//set newMateriality empty.
		const newMateriality = [];
		// If materiality is empty.
		if (currentMateriality) {
			newMateriality.push(currentMateriality);
		} else {
			const currentMateriality = {
				engagementId: engagementId,
				pmAmount: 0,
				teAmount: 0,
				sadNominalAmount: 0,
				isCurrent: true,
				isPriorYear: false
			};
			newMateriality.push(currentMateriality);
		}

		if (priorYearMateriality) {
			newMateriality.push(priorYearMateriality);
		} else {
			const priorYearMateriality = {
				engagementId: engagementId,
				pmAmount: 0,
				teAmount: 0,
				sadNominalAmount: 0,
				isCurrent: true,
				isPriorYear: true
			};
			newMateriality.push(priorYearMateriality);
		}

		setNewMateriality(newMateriality);
	}, [engagementId, currentMateriality, priorYearMateriality]);

	return (
		<AresSpecialBodyContainer
			bodyContent={bodyContent}
			headerId={headerId}
			sectionId={sectionId}
			bodyId={bodyId}
			updateBodyOptions={updateBodyOptions}
			getBodiesBySectionId={getBodiesBySectionId}
			isSnapshotView={isSnapshotView}
			showGuidance={showGuidance}
			showDescription={false}
		>
			<StyledMaterialityViewer className="StyledMaterialityViewer MaterialityViewerWrapper">
				{newMateriality &&
					newMateriality.length > 0 &&
					newMateriality.map((mat, index) => {
						return (
							<>
								<section className={mat.isCurrent ? 'CurrentMateriality' : 'PriorMateriality'}>
									<section className="materialityHeadingWrapper">
										<h4 className="materialityHeading">
											<EllipsesControl
												content={mat.isCurrent && mat.isPriorYear ? labels.PriorLabel : labels.currentLabel}
												tooltip={mat.isCurrent && mat.isPriorYear ? labels.PriorLabel : labels.currentLabel}
												noOfLines={1}
											/>
										</h4>
									</section>
									<section className="materialityBody">
										<section className="LabelWrapper">
											<section className="LabelTitle">
												<EllipsesControl
													content={labels.planningMaterialityLabel}
													tooltip={labels.planningMaterialityLabel}
													noOfLines={1}
												/>
											</section>
											<section className="LabelValue">
												{displayValue(mat.pmAmount)}
												<section className="DisplayPercentage">
													{displayPMPercentage(mat.pmAmount, getBasisAmount(mat))}
												</section>
											</section>
										</section>
										<section className="LabelWrapper">
											<section className="LabelTitle">
												<EllipsesControl
													content={labels.tolerableErrorLabel}
													tooltip={labels.tolerableErrorLabel}
													noOfLines={1}
												/>
											</section>
											<section className="LabelValue">
												{displayValue(mat.teAmount)}
												<section className="DisplayPercentage">
													{displayTEPercentage(mat.teAmount, mat.pmAmount)}
												</section>
											</section>
										</section>
										<section className="LabelWrapper">
											<section className="LabelTitle">
												<EllipsesControl content={labels.SADLabel} tooltip={labels.SADLabel} noOfLines={1} />
											</section>
											<section className="LabelValue">
												{displayValue(mat.sadNominalAmount)}
												<section className="DisplayPercentage">
													{displaySADPercentage(mat.sadNominalAmount, mat.pmAmount)}
												</section>
											</section>
										</section>
									</section>
								</section>
								{index + 1 < newMateriality.length && <section className="materialitySeparator" />}
							</>
						);
					})}
			</StyledMaterialityViewer>
		</AresSpecialBodyContainer>
	);
}

const StyledMaterialityViewer = styled.section`
	&.MaterialityViewerWrapper {
		display: flex;
		flex-direction: column;
		justify-content: space-between;
		width: 100%;
		border-radius: var(--px-12);
		border: var(--px-1) solid var(--neutrals-200);
		background: var(--neutrals-00white);
		color: var(--neutrals-900);
		padding: var(--px-24);
		position: relative;
		&:hover {
			box-shadow: var(--px-6) var(--px-6) var(--px-10) 0 rgba(46, 46, 56, 0.1);
		}
		.CurrentMateriality,
		.PriorMateriality {
			width: 100%;
			display: flex;
			flex-direction: column;
			.materialityHeadingWrapper {
				display: flex;
				align-items: center;
				.motif-icon {
					display: flex;
					svg {
						width: var(--px-24) !important;
						height: var(--px-24) !important;
						max-width: var(--px-24);
						max-height: var(--px-24);
					}
				}
			}
			.materialityHeading {
				margin: 0;
				font-size: var(--px-16);
				font-weight: 300;
				line-height: var(--px-24);
				color: var(--neutrals-1000);
				width: 100%;
			}
			.materialityBody {
				display: flex;
				flex-direction: row;
				justify-content: space-between;
			}
			.LabelWrapper {
				margin-top: var(--px-18);
				width: 33%;
				.LabelTitle {
					font-size: var(--px-14);
					font-weight: 700;
					line-height: var(--px-20);
					color: var(--neutrals-900);
					margin-right: var(--px-10);
				}
				.LabelValue {
					display: flex;
					font-size: var(--px-14);
					font-weight: 400;
					line-height: var(--px-20);
					margin-top: var(--px-12);
					.DisplayAmountCount {
						display: inline-flex;
						color: var(--neutrals-900);
						.ellipses {
							div {
								display: inline-flex;
								span {
									display: inline-block;
									width: 100%;
									text-overflow: ellipsis;
									overflow: hidden;
									white-space: nowrap;
								}
							}
						}
					}
					.DisplayPercentage {
						display: inline-flex;
						padding-left: var(--px-5);
						color: var(--neutrals-900);
						.DisplayAmountCount {
							color: var(--neutrals-900);
						}
						.ellipses {
							div {
								display: inline-flex;
								span {
									display: inline-block;
									width: 100%;
									text-overflow: ellipsis;
									overflow: hidden;
									white-space: nowrap;
								}
							}
						}
					}
				}
			}
		}
		.materialitySeparator {
			display: flex;
			margin: var(--px-18) 0;
			border-bottom: var(--px-1) solid var(--neutrals-200);
		}
		.PriorMateriality {
			background: var(--neutrals-100);
			padding-top: var(--px-10);
			h4 {
				margin: var(--px-10) 0;
			}
			.LabelWrapper {
				.LabelTitle {
					font-weight: 300 !important;
				}
				.LabelValue {
					.DisplayAmountCount {
						color: var(--neutrals-900) !important;
					}
				}
			}
		}
		.CurrentMateriality + .CurrentMateriality {
			margin-top: var(--px-30);
			.materialityHeadingWrapper {
				.materialityHeading {
					line-height: var(--px-20);
					width: 100%;
				}
			}
		}
	}
`;
